import { useState, useEffect, useCallback } from "react";
import passConfig from "@config/pass";
import { isQAEnv } from "@utils/util";
import wsuiApi from "@api/wsuiApi";
import type { IAgreementItem } from "@typing/wsui/agreement";
import { workBench } from "@config/path";
import { message } from "antd";
import { useUserStore } from "@store/userStore";

const passUrl = isQAEnv() ? passConfig.passStaticQA : passConfig.passStatic;

declare global {
    interface Window {
        passport?: any;
    }
}

// ========== 模块级变量（全局单例，所有 hook 实例共享） ==========
let passportInstance: any = null;
let agreementList: IAgreementItem[] = [];
let onLoginSuccessCallback: (() => void) | null = null;
let scriptLoadingPromise: Promise<void> | null = null;
let scriptLoaded = false;

const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
};

const fetchAgreementList = async () => {
    try {
        const { errno, data } = await wsuiApi.batchGetAgreement({businessType: 'doctor_youyi',});
        if (errno === 0 && data?.list) {
            agreementList = data.list;
        }
    } catch (error) {
        console.error("获取协议列表失败:", error);
    }
};

const signAgreements = async () => {
    if (agreementList.length === 0) return;
    try {
        const signData = agreementList.map((item) => ({
            agreement_id: item.agreement_id,
            version: item.version,
        }));
        await wsuiApi.agreementSign({ agreements: signData, businessType: 'doctor_youyi' });
    } catch (error) {
        console.error("签署协议失败:", error);
    }
};

/**
 * 初始化 passport 实例（只执行一次）
 * 动态创建挂载点，不依赖外部 DOM 元素
 */
const initPassportInstance = () => {
    if (!window?.passport?.pop || passportInstance) return;

    const mountDiv = document.createElement("div");
    mountDiv.style.display = "none";
    document.body.appendChild(mountDiv);

    passportInstance = window.passport.pop.init({
        id: mountDiv,
        loginVersion: "v5",
        composeTemplate: 1,
        apiOpt: {
            hasRegUrl: false,
            registerLink: "",
            loginType: 1,
            safeFlag: 0,
            product: "muzhidoctor",
            qrcode: 1,
            qrcode_animatin: 1,
            qrcodeLogin: 1,
            staticPage: passConfig.v3Jump,
            sms: 5,
            showAgreeCheckbox: 1,
        },
        // userpersonalText: agreementList[0]?.title ?? "隐私政策",
        // userpersonalLink: agreementList[0]?.url ?? "",
        // protocalText: agreementList[1]?.title ?? "平台服务协议",
        // protocalLink: agreementList[1]?.url ?? "",
        tangram: true,
        isPhone: false,
        hideLogo: 0,
        guideMapImg: passConfig.loginExample,
        qrcodeCfg: {
            appName: "百度健康超级工作台",
            downLoadQrcode: "https://muzhi-public-pic.cdn.bcebos.com/100110867047",
            appHref: "https://muzhi.baidu.com",
            appIcon: "https://muzhi-static.cdn.bcebos.com/doctorlogo.png",
        },
        onLoginSuccess: async (args: any) => {
            args.returnValue = false;
            passportInstance?.hide();
            await signAgreements();
            const { fetchUserInfo } = useUserStore.getState();
            await fetchUserInfo();
            onLoginSuccessCallback?.();
            onLoginSuccessCallback = null;
        },
        onShow: function () {
            const composeApi = document.querySelector("#passport-login-pop-api") as HTMLElement;
            if (composeApi) {
                composeApi.style.display = "flex";
                composeApi.style.alignItems = "flex-start";
            }

            const qrcodeDownload = document.querySelector(".pass-qrcode-download") as HTMLElement;

            if (qrcodeDownload && !qrcodeDownload.nextElementSibling?.classList.contains("pass-agreement-wrapper")) {
                const agreementLinks = agreementList
                    .map((item, index) => {
                        const separator = index < agreementList.length - 1 ? "和" : "";
                        return `<a href="${item.url}" target="_blank" style="text-decoration:none;color:#00BCBC;font-size:12px;">${item.title}</a>${separator}`;
                    })
                    .join("");
                qrcodeDownload.insertAdjacentHTML(
                    "afterend",
                    `<div class="pass-agreement-wrapper" style="text-align:center;margin-top:-4px;font-size:12px;color:#999;">登录成功代表您同意百度健康工作台${agreementLinks}</div>`
                );
            }
        },

    });
};

/**
 * Passport 登录 Hook
 *
 * 特性：
 * - 脚本只加载一次（模块级 scriptLoadingPromise 去重）
 * - SDK 只初始化一次（模块级 passportInstance 判断）
 * - showLogin 支持每次传入不同的登录成功回调
 * - 多个组件可同时调用，互不干扰
 */
export const usePassport = () => {
    const [loaded, setLoaded] = useState<boolean>(scriptLoaded);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        // 脚本已加载，直接初始化（确保当前实例也能 init）
        if (scriptLoaded) {
            initPassportInstance();
            return;
        }

        const load = async () => {
            if (!scriptLoadingPromise) {
                setLoading(true);
                scriptLoadingPromise = Promise.all([
                    loadScript(passUrl),
                    fetchAgreementList(),
                ]).then(() => {
                    scriptLoaded = true;
                });
            }

            try {
                await scriptLoadingPromise;
                initPassportInstance();
                setLoaded(true);
            } catch (error) {
                console.error("加载 Passport 脚本失败:", error);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    /**
     * 显示登录弹窗
     * @param onSuccess 登录成功后的回调，不同调用方可传入不同逻辑
     */
    const showLogin = useCallback((onSuccess?: () => void) => {
        if (!passportInstance) {
            console.warn("Passport 未初始化");
            return;
        }
        onLoginSuccessCallback = onSuccess ?? null;
        passportInstance.show();
    }, []);

    /**
     * 退出登录
     */
    const logout = useCallback(() => {
        message.success("已退出登录");
        setTimeout(() => {
            window.location.href = `${passUrl}/?logout&u=${encodeURIComponent(
                window.location.origin + workBench.home
            )}`;
        }, 150);
    }, []);

    return { loaded, loading, showLogin, logout };
};
